import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Landmark, Plus, Download, Wallet, Edit2, Trash2, RefreshCw, CreditCard, Search, ChevronUp, ChevronDown, ChevronRight, Layers, CalendarClock, AlertTriangle, Banknote, Split, BookOpen } from 'lucide-react';
import { clsx } from 'clsx';
import { PermissionGuard, usePermissions } from '@components/PermissionGuard';
import { DateInputBR } from '@components/DateInputBR';
import { companyService } from '@services/companyService';
import { financialService } from '@services/financialService';
import { providerService } from '@services/providerService';
import { CieloLedgerPanel } from './CieloLedgerPanel';
import { BankReconciliationPanel } from './BankReconciliationPanel';
import { CieloReconciliationGuide } from './CieloReconciliationGuide';
import { StatementProceduresGuide, STATEMENT_GUIDE_OVERDUE_ANCHOR } from './StatementProceduresGuide';
import { ItauStatementReconciliationPanel } from './ItauStatementReconciliationPanel';
import { CardSettlementDetailRow } from './CardSettlementDetailRow';
import { useUserPreferences, PREF_FIN_CHECKING_ACCOUNT } from '@hooks/useUserPreferences';
import { Link as RouterLink } from 'react-router-dom';
import type { Company } from '@/types/company';
import { formatCnpj } from '@/types/company';
import type { Provider } from '@/types/provider';
import type {
  FinancialAccount,
  FinancialTransaction,
  FinancialAllocationRequest,
  TransactionsStatement,
  GroupScope,
} from '@/types/financial';
import { formatAccountBankInfo } from '@/types/financial';
import {
  formatCurrencyBRL,
  formatDateBR,
  buildCategoryOptions,
  filterCategoryGroupsByMovement,
  activityTypeLabel,
  activityTypeBadgeColor,
  groupScopeLabel,
} from './financialUtils';
import { toCents } from './installmentCalc';
import { AllocationEditor } from './AllocationEditor';
import {
  allocationsValidationMessage,
  allocationSummary,
  linesFromAllocations,
  newAllocationLine,
  principalErpId,
  toAllocationPayload,
  type AllocationLine,
} from './allocationCalc';
import type { CategoryOptionGroup } from './financialUtils';
import {
  LoadingSpinner,
  ErrorBox,
  SuccessBox,
  PermissionFallback,
  SummaryCard,
  ModalShell,
  CategorySelect,
  selectCls,
  inputCls,
  labelCls,
  primaryBtnCls,
  secondaryBtnCls,
} from './financialShared';
import { InstallmentAmountFields } from './InstallmentAmountFields';

interface CheckingAccountPrefs extends Record<string, unknown> {
  company_id?: number;
  account_id?: number;
}

const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

/**
 * Identidade da linha prevista: ela não tem transactionId, e é única por
 * (data, bandeira) — que é exatamente o par que a descrição carrega.
 */
const projectionKey = (t: FinancialTransaction): string =>
  `p${t.transactionDate}|${t.description}`;

const getYearOptions = (): number[] => {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let y = currentYear - 5; y <= currentYear + 1; y++) years.push(y);
  return years;
};

// ─── Modal de lançamento (novo / editar) ────────────────────────

interface TransactionModalProps {
  transaction: FinancialTransaction | null; // null = novo
  scope: GroupScope;
  categoryGroups: CategoryOptionGroup[];
  onSave: (data: {
    description: string;
    transactionDate: string;
    amount: number;
    activityType: number;
    accountingPlanErpId: number | null;
    installments: number;
    providerId: number | null;
    /** null = sem rateio; lista = partes (mín. 2, soma = amount). */
    allocations: FinancialAllocationRequest[] | null;
  }) => Promise<void>;
  onClose: () => void;
}

const TransactionModal: React.FC<TransactionModalProps> = ({ transaction, scope, categoryGroups, onSave, onClose }) => {
  const isEdit = transaction != null;
  const isCardInvoiceTx = isEdit && transaction.idCreditCardsInvoices != null;

  const [description, setDescription] = useState(transaction?.description ?? '');
  const [transactionDate, setTransactionDate] = useState(
    transaction?.transactionDate?.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
  );
  const [amount, setAmount] = useState(transaction != null ? String(Math.abs(transaction.amount)) : '');
  const [activityType, setActivityType] = useState<number>(transaction?.activityType ?? 0);
  const [accountingPlanErpId, setAccountingPlanErpId] = useState<number | null>(transaction?.accountingPlanErpId ?? null);
  const [providerId, setProviderId] = useState<number | null>(transaction?.providerId ?? null);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [installments, setInstallments] = useState('1');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Rateio entre categorias. Não se aplica ao espelho de fatura (o valor é
  // reescrito pela fatura), ao depósito de adquirente (o detalhamento vem das
  // vendas) nem ao escopo "todas as parcelas" (cada parcela tem as suas partes).
  const canAllocate = !isCardInvoiceTx && transaction?.cardSettlementId == null && scope !== 'group';
  const allocateDisabledHint = isCardInvoiceTx
    ? 'Pagamento de fatura não pode ser rateado — rateie os itens dentro da fatura'
    : transaction?.cardSettlementId != null
      ? 'Depósito de adquirente não pode ser rateado'
      : scope === 'group'
        ? 'Rateio é por parcela — edite uma parcela por vez'
        : undefined;
  const [allocate, setAllocate] = useState<boolean>(Boolean(transaction?.allocated) && canAllocate);
  const [allocationLines, setAllocationLines] = useState<AllocationLine[]>(() =>
    transaction?.allocated ? linesFromAllocations(transaction.allocations) : [],
  );
  const allocationGroups = useMemo(
    () => filterCategoryGroupsByMovement(categoryGroups, activityType === 1 ? 1 : 0),
    [categoryGroups, activityType],
  );
  const totalCents = toCents(amount) ?? 0;

  const toggleAllocate = (on: boolean) => {
    setAllocate(on);
    if (on && allocationLines.length === 0) {
      // A categoria já escolhida vira a primeira parte; o usuário completa o resto.
      setAllocationLines([{ ...newAllocationLine(), accountingPlanErpId }, newAllocationLine()]);
    }
  };

  // Fornecedores para o campo de saída. Falha aqui fica silenciosa: o
  // lançamento continua salvável sem fornecedor.
  useEffect(() => {
    let active = true;
    providerService
      .getProviders({ page: 1, pageSize: 500 })
      .then((result) => {
        if (active) setProviders(result.data.filter((p) => p.is_active || p.provider_id === transaction?.providerId));
      })
      .catch(() => {
        if (active) setProviders([]);
      });
    return () => {
      active = false;
    };
  }, [transaction?.providerId]);

  const handleSubmit = async () => {
    setError(null);
    const parsedAmount = parseFloat(amount.replace(',', '.'));
    if (!description.trim()) {
      setError('Informe a descrição');
      return;
    }
    if (!transactionDate) {
      setError('Informe a data do lançamento');
      return;
    }
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Informe um valor positivo');
      return;
    }
    const parsedInstallments = parseInt(installments, 10);
    if (!isEdit && (isNaN(parsedInstallments) || parsedInstallments < 1)) {
      setError('Parcelas deve ser no mínimo 1');
      return;
    }
    // Rateio: a soma das partes tem de fechar o valor ao centavo — o back
    // recusa de qualquer forma, mas a tela avisa antes.
    if (allocate) {
      const msg = allocationsValidationMessage(totalCents, allocationLines);
      if (msg) {
        setError(msg);
        return;
      }
    }
    try {
      setSaving(true);
      await onSave({
        description: description.trim(),
        transactionDate,
        amount: parsedAmount,
        activityType,
        // Com rateio, o pai recebe a categoria principal (maior parte).
        accountingPlanErpId: allocate ? principalErpId(allocationLines) : accountingPlanErpId,
        installments: isEdit ? 1 : parsedInstallments,
        providerId: activityType === 0 ? providerId : null,
        allocations: allocate ? toAllocationPayload(allocationLines) : null,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar lançamento');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={isEdit ? 'Editar Lançamento' : 'Novo Lançamento'}
      icon={<Wallet size={20} className="text-emerald-600" />}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} disabled={saving} className={secondaryBtnCls}>
            Cancelar
          </button>
          <button type="button" onClick={handleSubmit} disabled={saving} className={primaryBtnCls}>
            {saving ? <RefreshCw size={14} className="animate-spin" /> : null}
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </>
      }
    >
      {isEdit && transaction.transactionGroup && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 rounded-lg p-3 text-xs">
          Escopo da edição: <span className="font-semibold">{groupScopeLabel(scope)}</span>
        </div>
      )}
      <div>
        <label className={labelCls}>Descrição *</label>
        <input
          className={inputCls}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={200}
          autoFocus
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Data *</label>
          <DateInputBR value={transactionDate} onChange={setTransactionDate} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Tipo *</label>
          <select className={selectCls} value={activityType} onChange={(e) => setActivityType(Number(e.target.value))}>
            <option value={0}>Saída</option>
            <option value={1}>Entrada</option>
          </select>
        </div>
      </div>
      {/* Total, quantidade e valor da parcela sempre sincronizados — editar
          qualquer um recalcula os outros dois. O payload segue sendo o TOTAL. */}
      <InstallmentAmountFields
        amount={amount}
        onAmountChange={setAmount}
        installments={installments}
        onInstallmentsChange={setInstallments}
        showInstallments={!isEdit}
      />
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className={labelCls.replace(' mb-1', '')}>Categoria</label>
          <label
            className={clsx('inline-flex items-center gap-1.5 text-xs', canAllocate ? 'text-indigo-700 cursor-pointer' : 'text-slate-400')}
            title={allocateDisabledHint}
          >
            <input
              type="checkbox"
              className="rounded border-slate-300"
              checked={allocate}
              disabled={!canAllocate}
              onChange={(e) => toggleAllocate(e.target.checked)}
            />
            <Split size={12} /> Ratear entre categorias
          </label>
        </div>
        {allocate ? (
          <>
            <AllocationEditor
              totalCents={totalCents}
              categoryGroups={allocationGroups}
              lines={allocationLines}
              onChange={setAllocationLines}
            />
            {!isEdit && parseInt(installments, 10) > 1 && (
              <p className="text-xs text-slate-500 mt-1">
                O rateio será replicado proporcionalmente em cada parcela.
              </p>
            )}
          </>
        ) : (
          <CategorySelect
            groups={categoryGroups}
            value={accountingPlanErpId}
            onChange={setAccountingPlanErpId}
            disabled={isCardInvoiceTx}
          />
        )}
        {isCardInvoiceTx && (
          <p className="text-xs text-slate-400 mt-1">
            Lançamento vinculado a fatura de cartão — a categoria não pode ser alterada.
          </p>
        )}
      </div>
      {/* Só saída tem fornecedor: é a dimensão do relatório de Fornecedores,
          que concilia o que foi pago com as parcelas dos pedidos de compra. */}
      {activityType === 0 && !isCardInvoiceTx && (
        <div>
          <label className={labelCls}>Fornecedor</label>
          <select
            className={selectCls}
            value={providerId ?? ''}
            onChange={(e) => setProviderId(e.target.value === '' ? null : Number(e.target.value))}
          >
            <option value="">— Sem fornecedor —</option>
            {providers.map((p) => (
              <option key={p.provider_id} value={p.provider_id}>
                {p.trade_name || p.legal_name}
              </option>
            ))}
          </select>
          <p className="text-xs text-slate-400 mt-1">Usado no relatório de Fornecedores (Relatórios › Financeiro).</p>
        </div>
      )}
      {error && <ErrorBox message={error} />}
    </ModalShell>
  );
};

// ─── Modal Registrar Saldo ──────────────────────────────────────

const BalanceModal: React.FC<{
  onSave: (balanceDate: string, amount: number) => Promise<void>;
  onClose: () => void;
}> = ({ onSave, onClose }) => {
  const [balanceDate, setBalanceDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    const parsed = parseFloat(amount.replace(',', '.'));
    if (!balanceDate) {
      setError('Informe a data do saldo');
      return;
    }
    if (isNaN(parsed)) {
      setError('Informe o valor do saldo');
      return;
    }
    try {
      setSaving(true);
      await onSave(balanceDate, parsed);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao registrar saldo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title="Registrar Saldo"
      icon={<Landmark size={20} className="text-emerald-600" />}
      onClose={onClose}
      maxWidth="max-w-md"
      footer={
        <>
          <button type="button" onClick={onClose} disabled={saving} className={secondaryBtnCls}>
            Cancelar
          </button>
          <button type="button" onClick={handleSubmit} disabled={saving} className={primaryBtnCls}>
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </>
      }
    >
      <div>
        <label className={labelCls}>Data do saldo *</label>
        <DateInputBR value={balanceDate} onChange={setBalanceDate} className={inputCls} />
      </div>
      <div>
        <label className={labelCls}>Valor (R$) *</label>
        <input
          className={inputCls}
          type="number"
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0,00"
          autoFocus
        />
      </div>
      {error && <ErrorBox message={error} />}
    </ModalShell>
  );
};

// ─── Prompt de escopo (parcelas) ────────────────────────────────

const ScopePromptModal: React.FC<{
  action: 'edit' | 'delete';
  onChoose: (scope: GroupScope) => void;
  onClose: () => void;
}> = ({ action, onChoose, onClose }) => (
  <ModalShell
    title="Aplicar a todas as parcelas?"
    onClose={onClose}
    maxWidth="max-w-md"
    footer={
      <button type="button" onClick={onClose} className={secondaryBtnCls}>
        Cancelar
      </button>
    }
  >
    <p className="text-sm text-slate-600">
      Este lançamento faz parte de um grupo de parcelas. Deseja {action === 'edit' ? 'editar' : 'excluir'}{' '}
      somente este lançamento ou todas as parcelas do grupo?
    </p>
    <div className="flex flex-col gap-2">
      <button type="button" onClick={() => onChoose('single')} className={secondaryBtnCls + ' justify-center'}>
        {groupScopeLabel('single')}
      </button>
      <button
        type="button"
        onClick={() => onChoose('group')}
        className={clsx(
          'inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors',
          action === 'delete' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white',
        )}
      >
        {groupScopeLabel('group')}
      </button>
    </div>
  </ModalShell>
);

// ─── View Principal ─────────────────────────────────────────────

export const FinancialTransactionsView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('financial.transactions.write').hasPermission;
  const canBankStatement = checkPermission('financial.bank_statement.read').hasPermission;

  const now = new Date();
  // A tela alterna entre o extrato, os recebíveis de cartão e a conciliação
  // via API do Itaú, sempre no contexto da conta selecionada acima.
  const [view, setView] = useState<'statement' | 'cielo' | 'itau' | 'guide'>('statement');
  // Seção do guia de procedimentos a abrir (o link "Como resolver" da linha vencida).
  const [guideSection, setGuideSection] = useState<string | undefined>(undefined);
  const [cieloMode, setCieloMode] = useState<'settlement' | 'bank' | 'guide'>('settlement');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [accounts, setAccounts] = useState<FinancialAccount[]>([]);
  const [accountId, setAccountId] = useState<number | null>(null); // idAccountsMain
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const { prefs, loaded: prefsLoaded, save: savePrefs } = useUserPreferences<CheckingAccountPrefs>(PREF_FIN_CHECKING_ACCOUNT);
  const accountRestored = useRef(false);

  const [statement, setStatement] = useState<TransactionsStatement | null>(null);
  const [categoryGroups, setCategoryGroups] = useState<CategoryOptionGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [txModal, setTxModal] = useState<{ transaction: FinancialTransaction | null; scope: GroupScope } | null>(null);
  const [balanceModalOpen, setBalanceModalOpen] = useState(false);
  const [scopePrompt, setScopePrompt] = useState<{ action: 'edit' | 'delete'; transaction: FinancialTransaction } | null>(null);
  const [busy, setBusy] = useState(false);

  // Lançamentos com o depósito da adquirente aberto. O detalhe é buscado só
  // quando a linha é expandida: carregar tudo de antemão seria uma consulta por
  // lançamento num extrato de mês inteiro.
  const [expandedSettlements, setExpandedSettlements] = useState<Set<number>>(new Set());

  const toggleSettlement = (transactionId: number) => {
    setExpandedSettlements((prev) => {
      const next = new Set(prev);
      if (next.has(transactionId)) next.delete(transactionId);
      else next.add(transactionId);
      return next;
    });
  };

  // A previsão não tem id de lançamento: a chave é (data, bandeira), que é o que
  // a descrição carrega e a unidade em que ela é agrupada.
  const [expandedProjections, setExpandedProjections] = useState<Set<string>>(new Set());

  const toggleProjection = (key: string) => {
    setExpandedProjections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Busca + ordenação (client-side, sobre o extrato já carregado)
  const [search, setSearch] = useState('');
  const [sortColumn, setSortColumn] = useState<'transactionDate' | 'amount' | 'categoryLabel'>('transactionDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Agrupamento por data: um cabeçalho por dia (lançamentos, entradas/saídas e
  // saldo do dia), com as linhas recolhidas até o usuário expandir.
  const [groupByDate, setGroupByDate] = useState(false);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());

  const handleSort = (column: 'transactionDate' | 'amount' | 'categoryLabel') => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (column: 'transactionDate' | 'amount' | 'categoryLabel') => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc'
      ? <ChevronUp size={13} className="text-emerald-600" />
      : <ChevronDown size={13} className="text-emerald-600" />;
  };


  // Empresas — espera as preferências para não selecionar a primeira empresa antes da restaurada
  useEffect(() => {
    if (!prefsLoaded) return;
    companyService
      .getCompanies()
      .then((list) => {
        setCompanies(list);
        if (list.length === 0) return;
        const saved = list.find((c) => c.company_id === prefs?.company_id);
        setCompanyId(saved?.company_id ?? list[0].company_id);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar empresas'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsLoaded]);

  // Contas + plano de contas da empresa
  useEffect(() => {
    if (companyId == null) return;
    setAccounts([]);
    setAccountId(null);
    setStatement(null);
    financialService
      .getAccounts(companyId)
      .then((list) => {
        setAccounts(list);
        if (list.length === 0) return;
        // Restaura a conta salva apenas na primeira carga; ao trocar de empresa vale a primeira conta dela.
        const saved = accountRestored.current
          ? undefined
          : list.find((a) => a.idAccountsMain === prefs?.account_id);
        accountRestored.current = true;
        setAccountId(saved?.idAccountsMain ?? list[0].idAccountsMain);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar contas'));
    financialService
      .getAccountingPlans(companyId)
      .then((tree) => setCategoryGroups(buildCategoryOptions(tree)))
      .catch(() => setCategoryGroups([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Persiste os filtros (período fica sempre no mês corrente)
  useEffect(() => {
    if (!prefsLoaded || companyId == null) return;
    savePrefs({ company_id: companyId, account_id: accountId ?? undefined });
  }, [prefsLoaded, companyId, accountId, savePrefs]);

  // Recarrega a lista de contas (atualiza o Saldo Atual) sem resetar a seleção
  const reloadAccounts = useCallback(async () => {
    if (companyId == null) return;
    try {
      const list = await financialService.getAccounts(companyId);
      setAccounts(list);
    } catch {
      /* mantém a lista atual em caso de falha */
    }
  }, [companyId]);

  // Extrato
  const loadStatement = useCallback(async () => {
    if (accountId == null) return;
    setLoading(true);
    setError(null);
    try {
      const data = await financialService.getTransactions(accountId, year, month);
      setStatement(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar lançamentos');
    } finally {
      setLoading(false);
    }
  }, [accountId, year, month]);

  useEffect(() => {
    loadStatement();
  }, [loadStatement]);

  // Alterna entre o extrato e os painéis (Cielo/Itaú). Ao VOLTAR para o
  // extrato, recarrega: os dois painéis criam lançamentos e liquidam
  // recebíveis — sem o reload, a tela seguia mostrando linhas "Previsto" que
  // acabaram de virar realizado na conciliação.
  const toggleView = (target: 'cielo' | 'itau' | 'guide') => {
    const next = view === target ? 'statement' : target;
    if (target === 'guide') setGuideSection(undefined);
    setView(next);
    if (next === 'statement') void loadStatement();
  };

  const selectedAccount = useMemo(
    () => accounts.find((a) => a.idAccountsMain === accountId) ?? null,
    [accounts, accountId],
  );

  const totals = useMemo(() => {
    // Só o realizado. A previsão não tem activityType — sem este recorte ela
    // caía no `!== 1` e era somada às SAÍDAS, virando dinheiro a receber
    // contabilizado como dinheiro que saiu.
    const transactions = (statement?.transactions ?? []).filter((t) => !t.projected);
    const entradas = transactions
      .filter((t) => t.activityType === 1)
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);
    const saidas = transactions
      .filter((t) => t.activityType !== 1)
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);
    // O previsto é o que AINDA vai entrar. A parcela vencida sai daqui e vira
    // número próprio: ela não é caixa a caminho — ou o crédito já entrou e falta
    // a baixa (o caso comum), ou o dinheiro não veio. Somá-la ao previsto
    // contaria de novo um depósito que já está no realizado.
    const projetadas = (statement?.transactions ?? []).filter((t) => t.projected);
    const previsto = projetadas
      .filter((t) => !t.overdue)
      .reduce((sum, t) => sum + t.amount, 0);
    const naoRecebido = projetadas
      .filter((t) => t.overdue)
      .reduce((sum, t) => sum + t.amount, 0);
    return { entradas, saidas, previsto, naoRecebido };
  }, [statement]);

  /**
   * Existe previsão neste mês?
   *
   * A coluna de saldo projetado e o cabeçalho só aparecem quando há o que
   * projetar: numa conta sem recebíveis de cartão, ela seria a repetição exata
   * da coluna ao lado.
   */
  const hasProjection = useMemo(
    () => (statement?.transactions ?? []).some((t) => t.projected),
    [statement],
  );

  /** Colunas da tabela, para o colSpan das linhas expandidas acompanhar. */
  const columnCount = 6 + (hasProjection ? 1 : 0) + (canWrite ? 1 : 0);

  /** Lançamentos rateados com as partes abertas na tela. */
  const [expandedAllocations, setExpandedAllocations] = useState<Set<number>>(new Set());
  const toggleAllocations = (transactionId: number) =>
    setExpandedAllocations((prev) => {
      const next = new Set(prev);
      if (next.has(transactionId)) next.delete(transactionId);
      else next.add(transactionId);
      return next;
    });

  // Linhas visíveis: filtro por descrição/categoria/data + ordenação por data/valor/categoria.
  // A busca por data casa tanto o valor ISO quanto o formato brasileiro (dd/mm/aaaa).
  const visibleTransactions = useMemo(() => {
    const rows = statement?.transactions ?? [];
    const term = search.trim().toLowerCase();
    const filtered = term
      ? rows.filter((t) =>
          (t.description ?? '').toLowerCase().includes(term) ||
          (t.categoryLabel ?? '').toLowerCase().includes(term) ||
          (t.allocated && allocationSummary(t.allocations ?? []).toLowerCase().includes(term)) ||
          (t.transactionDate ?? '').toLowerCase().includes(term) ||
          formatDateBR(t.transactionDate).toLowerCase().includes(term),
        )
      : rows;

    // Agrupado por data, a ordem TEM de ser cronológica: cabeçalhos de dia
    // sobre uma lista ordenada por valor intercalariam o mesmo dia várias vezes.
    const effectiveColumn = groupByDate ? 'transactionDate' : sortColumn;
    const effectiveDirection = groupByDate ? 'asc' : sortDirection;
    const sorted = [...filtered].sort((a, b) => {
      let cmp: number;
      if (effectiveColumn === 'amount') {
        cmp = a.amount - b.amount;
      } else if (effectiveColumn === 'categoryLabel') {
        cmp = (a.categoryLabel ?? '').localeCompare(b.categoryLabel ?? '', 'pt-BR');
      } else {
        cmp = (a.transactionDate ?? '').localeCompare(b.transactionDate ?? '', 'pt-BR');
      }
      return effectiveDirection === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [statement, search, sortColumn, sortDirection, groupByDate]);

  // Agregados por dia para os cabeçalhos do agrupamento: contagem, entradas e
  // saídas realizadas (previsão fica de fora dos totais, como nos cards), e o
  // saldo em que o dia termina — o da última linha do dia.
  const dayAggregates = useMemo(() => {
    const map = new Map<string, { count: number; entradas: number; saidas: number; running: number; projectedBalance: number }>();
    if (!groupByDate) return map;
    for (const t of visibleTransactions) {
      const key = (t.transactionDate ?? '').slice(0, 10);
      let agg = map.get(key);
      if (!agg) {
        agg = { count: 0, entradas: 0, saidas: 0, running: 0, projectedBalance: 0 };
        map.set(key, agg);
      }
      agg.count++;
      if (!t.projected) {
        if (t.amount >= 0) agg.entradas += t.amount;
        else agg.saidas += -t.amount;
      }
      agg.running = t.runningBalance;
      agg.projectedBalance = t.projectedBalance ?? t.runningBalance;
    }
    return map;
  }, [groupByDate, visibleTransactions]);

  // Chaves de linha que ABREM um dia novo — é onde o cabeçalho entra.
  const firstRowOfDate = useMemo(() => {
    const set = new Set<string>();
    if (!groupByDate) return set;
    let previous = '';
    for (const t of visibleTransactions) {
      const key = (t.transactionDate ?? '').slice(0, 10);
      if (key !== previous) {
        set.add(t.projected ? projectionKey(t) : `t${t.transactionId}`);
        previous = key;
      }
    }
    return set;
  }, [groupByDate, visibleTransactions]);

  const toggleExpandedDate = (date: string) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  // ── Ações ───────────────────────────────────────────────────

  const handleSaveTransaction = async (data: {
    description: string;
    transactionDate: string;
    amount: number;
    activityType: number;
    accountingPlanErpId: number | null;
    installments: number;
    providerId: number | null;
    allocations: FinancialAllocationRequest[] | null;
  }) => {
    if (companyId == null || accountId == null) throw new Error('Selecione empresa e conta');
    if (txModal?.transaction) {
      await financialService.updateTransaction(txModal.transaction.transactionId, txModal.scope, {
        description: data.description,
        transactionDate: data.transactionDate,
        amount: data.amount,
        activityType: data.activityType,
        accountingPlanErpId: data.accountingPlanErpId,
        providerId: data.providerId,
        // Tri-state do PUT: a tela sempre diz o que quer — [] remove o rateio.
        allocations: data.allocations ?? [],
      });
      setNotice('Lançamento atualizado com sucesso');
    } else {
      await financialService.createTransaction({
        companyId,
        idAccountsMain: accountId,
        description: data.description,
        transactionDate: data.transactionDate,
        amount: data.amount,
        activityType: data.activityType,
        accountingPlanErpId: data.accountingPlanErpId,
        providerId: data.providerId,
        ...(data.installments > 1 ? { installments: data.installments } : {}),
        ...(data.allocations ? { allocations: data.allocations } : {}),
      });
      setNotice('Lançamento criado com sucesso');
    }
    await Promise.all([loadStatement(), reloadAccounts()]);
  };

  const handleEditClick = (transaction: FinancialTransaction) => {
    // Parcela rateada é editada uma a uma — o escopo "todas as parcelas"
    // gravaria o mesmo valor/categoria em todas (o back também recusa).
    if (transaction.transactionGroup && !transaction.allocated) {
      setScopePrompt({ action: 'edit', transaction });
    } else {
      setTxModal({ transaction, scope: 'single' });
    }
  };

  const performDelete = async (transaction: FinancialTransaction, scope: GroupScope) => {
    setBusy(true);
    setError(null);
    try {
      await financialService.deleteTransaction(transaction.transactionId, scope);
      setNotice('Lançamento excluído com sucesso');
      await Promise.all([loadStatement(), reloadAccounts()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir lançamento');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteClick = (transaction: FinancialTransaction) => {
    if (transaction.transactionGroup) {
      setScopePrompt({ action: 'delete', transaction });
      return;
    }
    // Excluir o mês de uma recorrência é definitivo: o processo diário passa a
    // pular essa competência. Vale avisar antes, porque o comportamento antigo
    // (o lançamento voltar sozinho) era o que as pessoas conheciam.
    const aviso =
      transaction.recurringTransactionId != null
        ? '\n\nEste mês não será recriado pelo processo diário de recorrências.'
        : '';
    if (window.confirm(`Excluir o lançamento "${transaction.description}"?${aviso}`)) {
      performDelete(transaction, 'single');
    }
  };

  const handleScopeChosen = (scope: GroupScope) => {
    if (!scopePrompt) return;
    const { action, transaction } = scopePrompt;
    setScopePrompt(null);
    if (action === 'edit') {
      setTxModal({ transaction, scope });
    } else {
      performDelete(transaction, scope);
    }
  };

  const handleRegisterBalance = async (balanceDate: string, amount: number) => {
    if (accountId == null) throw new Error('Selecione uma conta');
    await financialService.registerBalance(accountId, { balanceDate, amount });
    setNotice('Saldo registrado com sucesso');
    await Promise.all([loadStatement(), reloadAccounts()]);
  };

  // O "Upload Cielo" foi REMOVIDO desta tela: ele lançava a planilha direto na
  // conta corrente, sem conferir contra a agenda de recebíveis e sem dedup —
  // reimportar o mesmo arquivo duplicava tudo. O caminho agora é
  // Relatórios → Financeiro → Recebíveis de Cartão → Conferir planilha da Cielo,
  // que compara antes de gravar e deixa a decisão com o usuário.

  const handleExportCsv = async () => {
    if (accountId == null) return;
    setBusy(true);
    setError(null);
    try {
      await financialService.exportTransactionsCsv(accountId, year, month);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao exportar CSV');
    } finally {
      setBusy(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────

  return (
    <PermissionGuard permission="financial.transactions.read" fallback={<PermissionFallback label="a conta corrente" />}>
      <div className="space-y-6">
        {/* Modais */}
        {txModal && (
          <TransactionModal
            transaction={txModal.transaction}
            scope={txModal.scope}
            categoryGroups={categoryGroups}
            onSave={handleSaveTransaction}
            onClose={() => setTxModal(null)}
          />
        )}
        {balanceModalOpen && <BalanceModal onSave={handleRegisterBalance} onClose={() => setBalanceModalOpen(false)} />}
        {scopePrompt && (
          <ScopePromptModal
            action={scopePrompt.action}
            onChoose={handleScopeChosen}
            onClose={() => setScopePrompt(null)}
          />
        )}

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Conta Corrente</h1>
            <p className="text-sm text-slate-500 mt-1">Extrato e lançamentos das contas bancárias</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {canWrite && (
              <>
                <button
                  className={primaryBtnCls}
                  onClick={() => setTxModal({ transaction: null, scope: 'single' })}
                  disabled={accountId == null}
                >
                  <Plus size={16} /> Novo Lançamento
                </button>
                <button className={secondaryBtnCls} onClick={() => setBalanceModalOpen(true)} disabled={accountId == null}>
                  <Landmark size={16} /> Registrar Saldo
                </button>
                {/* Recebíveis de cartão vivem aqui, e não em Relatórios, porque
                    geram lançamento NESTA conta — a conta selecionada acima é a
                    que será afetada. */}
                <button
                  className={secondaryBtnCls}
                  onClick={() => toggleView('cielo')}
                  disabled={accountId == null}
                >
                  <CreditCard size={16} /> {view === 'cielo' ? 'Voltar aos lançamentos' : 'Recebíveis Cielo'}
                </button>
              </>
            )}
            {/* Conciliação via API do Itaú: só para conta com integração ativa
                (credencial + certificado carregados no cadastro da conta). */}
            {canBankStatement && selectedAccount?.bankIntegrationActive && (
              <button
                className={secondaryBtnCls}
                onClick={() => toggleView('itau')}
                disabled={accountId == null}
              >
                <Banknote size={16} /> {view === 'itau' ? 'Voltar aos lançamentos' : 'Conciliação Itaú'}
              </button>
            )}
            {/* Procedimentos: o passo a passo do que fazer quando o extrato não
                bate (previsto não recebido, crédito sem previsão, duplicado). */}
            <button className={secondaryBtnCls} onClick={() => toggleView('guide')}>
              <BookOpen size={16} /> {view === 'guide' ? 'Voltar aos lançamentos' : 'Procedimentos'}
            </button>
            <button className={secondaryBtnCls} onClick={handleExportCsv} disabled={busy || accountId == null}>
              <Download size={16} /> Exportar CSV
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className={labelCls}>Empresa</label>
              <select
                className={selectCls + ' w-64'}
                value={companyId ?? ''}
                onChange={(e) => setCompanyId(e.target.value ? Number(e.target.value) : null)}
              >
                {companies.map((c) => (
                  <option key={c.company_id} value={c.company_id}>
                    {formatCnpj(c.document)} — {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Conta</label>
              <select
                className={selectCls + ' w-64'}
                value={accountId ?? ''}
                onChange={(e) => setAccountId(e.target.value ? Number(e.target.value) : null)}
              >
                {accounts.length === 0 && <option value="">Nenhuma conta disponível</option>}
                {accounts.map((a) => {
                  const bankInfo = formatAccountBankInfo(a);
                  return (
                    <option key={a.idAccountsMain} value={a.idAccountsMain}>
                      {a.accountsDescription}
                      {a.bankName ? ` — ${a.bankName}` : ''}
                      {bankInfo ? ` (${bankInfo})` : ''}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className={labelCls}>Mês</label>
              <select className={selectCls + ' w-36'} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                {MONTHS.map((m, idx) => (
                  <option key={m} value={idx + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Ano</label>
              <select className={selectCls + ' w-28'} value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {getYearOptions().map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <label className="inline-flex items-center gap-2 pb-2 text-sm text-slate-600 cursor-pointer select-none">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                checked={groupByDate}
                onChange={(e) => {
                  setGroupByDate(e.target.checked);
                  // Começa tudo recolhido: o valor do agrupamento é a visão
                  // resumida — expandir é decisão por dia.
                  setExpandedDates(new Set());
                }}
              />
              Agrupar por data
            </label>
          </div>
        </div>

        {notice && <SuccessBox message={notice} onDismiss={() => setNotice(null)} />}
        {error && <ErrorBox message={error} onRetry={loadStatement} />}
        {/* Recebíveis de cartão: conferir a planilha da adquirente e conciliar
            com o extrato. Fica NESTA tela porque o resultado é lançamento nesta
            conta corrente, e a conta precisa estar à vista de quem importa. */}
        {view === 'cielo' && companyId != null && accountId != null && selectedAccount != null && (
          <div className="space-y-4">
            <div className="inline-flex rounded-lg border border-slate-200 p-1">
              <button
                onClick={() => setCieloMode('settlement')}
                className={clsx(
                  'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                  cieloMode === 'settlement' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600 hover:text-slate-800',
                )}
              >
                Conferir planilha da Cielo
              </button>
              <button
                onClick={() => setCieloMode('bank')}
                className={clsx(
                  'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                  cieloMode === 'bank' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600 hover:text-slate-800',
                )}
              >
                Conciliar com o extrato
              </button>
              <button
                onClick={() => setCieloMode('guide')}
                className={clsx(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                  cieloMode === 'guide' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600 hover:text-slate-800',
                )}
              >
                <BookOpen size={14} /> Como conciliar
              </button>
            </div>

            {cieloMode === 'settlement' && (
              <CieloLedgerPanel
                companyId={companyId}
                accountId={accountId}
                companyName={companies.find((c) => c.company_id === companyId)?.name ?? ''}
                accountLabel={selectedAccount.accountsDescription}
              />
            )}
            {cieloMode === 'bank' && (
              <BankReconciliationPanel
                companyId={companyId}
                accountId={accountId}
                accountLabel={selectedAccount.accountsDescription}
              />
            )}
            {cieloMode === 'guide' && (
              <CieloReconciliationGuide
                onOpenSettlement={() => setCieloMode('settlement')}
                onOpenBank={() => setCieloMode('bank')}
                onOpenItau={
                  canBankStatement && selectedAccount.bankIntegrationActive ? () => toggleView('itau') : undefined
                }
              />
            )}
          </div>
        )}

        {/* Conciliação bancária via API do Itaú: o extrato vem direto do banco
            e o usuário decide o que fazer com cada evento. Repare que os
            endpoints usam o AccountId (PK do cadastro), não o idAccountsMain. */}
        {view === 'itau' && selectedAccount != null && (
          <ItauStatementReconciliationPanel
            accountId={selectedAccount.accountId}
            accountLabel={selectedAccount.accountsDescription}
            categoryGroups={categoryGroups}
          />
        )}

        {view === 'guide' && (
          <StatementProceduresGuide
            initialSection={guideSection}
            onOpenCielo={() => toggleView('cielo')}
            onOpenItau={
              canBankStatement && selectedAccount?.bankIntegrationActive ? () => toggleView('itau') : undefined
            }
          />
        )}

        {view === 'statement' && loading && <LoadingSpinner />}

        {view === 'statement' && !loading && statement && (
          <>
            {/* Cards de resumo */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <SummaryCard
                label="Saldo Inicial"
                value={formatCurrencyBRL(statement.openingBalance)}
                valueClassName={statement.openingBalance < 0 ? 'text-red-600' : 'text-slate-900'}
              />
              <SummaryCard label="Entradas" value={formatCurrencyBRL(totals.entradas)} valueClassName="text-emerald-600" />
              <SummaryCard label="Saídas" value={formatCurrencyBRL(totals.saidas)} valueClassName="text-red-600" />
              <SummaryCard
                label="Saldo Final"
                value={formatCurrencyBRL(statement.closingBalance)}
                valueClassName={statement.closingBalance < 0 ? 'text-red-600' : 'text-emerald-600'}
              />
              {selectedAccount != null && (
                <SummaryCard
                  label="Saldo Atual"
                  value={formatCurrencyBRL(selectedAccount.currentBalance)}
                  valueClassName={selectedAccount.currentBalance < 0 ? 'text-red-600' : 'text-emerald-600'}
                />
              )}
            </div>

            {/* A previsão fica FORA dos cards de realizado: Entradas, Saídas e
                Saldo Final são o que aconteceu. Misturar os dois no mesmo card
                tiraria do usuário a capacidade de conferir a conta. */}
            {hasProjection && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <SummaryCard
                  label="Previsto a receber no mês"
                  value={formatCurrencyBRL(totals.previsto)}
                  valueClassName="text-sky-700"
                />
                <SummaryCard
                  label="Saldo final com previsão"
                  value={formatCurrencyBRL(statement.closingBalance + totals.previsto)}
                  valueClassName={statement.closingBalance + totals.previsto < 0 ? 'text-red-600' : 'text-sky-700'}
                />
                {/* Fora do previsto e fora do saldo, de propósito: é pendência
                    de conciliação, não caixa a caminho. Ganha card próprio
                    porque é justamente o que precisa de ação. */}
                {totals.naoRecebido !== 0 && (
                  <SummaryCard
                    label="Vencido não recebido"
                    value={formatCurrencyBRL(totals.naoRecebido)}
                    valueClassName="text-amber-600"
                  />
                )}
              </div>
            )}

            {/* Busca */}
            <div className="relative max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className={inputCls + ' pl-9'}
                placeholder="Buscar por descrição, categoria ou data..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Tabela */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th
                        className="text-left py-2 px-3 font-semibold text-slate-600 cursor-pointer hover:bg-slate-100 select-none"
                        onClick={() => handleSort('transactionDate')}
                      >
                        <span className="inline-flex items-center gap-1">Data{renderSortIcon('transactionDate')}</span>
                      </th>
                      <th className="text-left py-2 px-3 font-semibold text-slate-600">Descrição</th>
                      <th
                        className="text-left py-2 px-3 font-semibold text-slate-600 cursor-pointer hover:bg-slate-100 select-none"
                        onClick={() => handleSort('categoryLabel')}
                      >
                        <span className="inline-flex items-center gap-1">Categoria{renderSortIcon('categoryLabel')}</span>
                      </th>
                      <th className="text-center py-2 px-3 font-semibold text-slate-600">Tipo</th>
                      <th
                        className="text-right py-2 px-3 font-semibold text-slate-600 cursor-pointer hover:bg-slate-100 select-none"
                        onClick={() => handleSort('amount')}
                      >
                        <span className="inline-flex items-center justify-end gap-1">Valor{renderSortIcon('amount')}</span>
                      </th>
                      <th className="text-right py-2 px-3 font-semibold text-slate-600">
                        Saldo
                        <span className="block text-[10px] font-normal text-slate-400">realizado</span>
                      </th>
                      {/* O saldo projetado só aparece quando existe previsão no
                          mês — numa conta sem recebíveis a coluna seria uma
                          repetição da anterior. */}
                      {hasProjection && (
                        <th className="text-right py-2 px-3 font-semibold text-sky-700">
                          Saldo
                          <span className="block text-[10px] font-normal text-sky-500">com previsão</span>
                        </th>
                      )}
                      {canWrite && <th className="text-right py-2 px-3 font-semibold text-slate-600 w-24">Ações</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleTransactions.length === 0 && (
                      <tr>
                        <td colSpan={columnCount} className="py-10 text-center text-slate-400">
                          {statement.transactions.length === 0
                            ? 'Nenhum lançamento no período'
                            : 'Nenhum lançamento corresponde à busca'}
                        </td>
                      </tr>
                    )}
                    {visibleTransactions.map((t) => {
                      /* Linha prevista não tem transactionId — usar só ele como
                         chave faria todas as previsões compartilharem a chave 0,
                         e o React embaralharia as linhas. A previsão é única por
                         (data, bandeira), que é o que a descrição carrega. */
                      const rowKey = t.projected ? projectionKey(t) : `t${t.transactionId}`;
                      const dateKey = (t.transactionDate ?? '').slice(0, 10);
                      const dayAgg = groupByDate && firstRowOfDate.has(rowKey) ? dayAggregates.get(dateKey) : null;
                      const rowVisible = !groupByDate || expandedDates.has(dateKey);
                      return (
                      <React.Fragment key={rowKey}>
                      {/* Cabeçalho do dia: resumo clicável; as linhas ficam
                          recolhidas até o usuário expandir a data. */}
                      {dayAgg && (
                        <tr
                          className="bg-slate-100/80 border-b border-slate-200 cursor-pointer hover:bg-slate-200/60 select-none"
                          onClick={() => toggleExpandedDate(dateKey)}
                        >
                          <td className="py-2 px-3 whitespace-nowrap font-semibold text-slate-700">
                            <span className="inline-flex items-center gap-1">
                              {expandedDates.has(dateKey) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              {formatDateBR(t.transactionDate)}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-slate-500" colSpan={2}>
                            {dayAgg.count} lançamento{dayAgg.count > 1 ? 's' : ''}
                          </td>
                          <td className="py-2 px-3" />
                          <td className="py-2 px-3 text-right whitespace-nowrap text-xs">
                            {dayAgg.entradas > 0 && <span className="text-emerald-600 font-medium">+{formatCurrencyBRL(dayAgg.entradas)}</span>}
                            {dayAgg.entradas > 0 && dayAgg.saidas > 0 && <span className="text-slate-400"> · </span>}
                            {dayAgg.saidas > 0 && <span className="text-red-600 font-medium">−{formatCurrencyBRL(dayAgg.saidas)}</span>}
                            {dayAgg.entradas === 0 && dayAgg.saidas === 0 && <span className="text-slate-400">—</span>}
                          </td>
                          <td className={clsx('py-2 px-3 text-right whitespace-nowrap font-medium', dayAgg.running < 0 ? 'text-red-600' : 'text-emerald-600')}>
                            {formatCurrencyBRL(dayAgg.running)}
                          </td>
                          {hasProjection && (
                            <td className={clsx('py-2 px-3 text-right whitespace-nowrap font-medium', dayAgg.projectedBalance < 0 ? 'text-red-600' : 'text-sky-700')}>
                              {formatCurrencyBRL(dayAgg.projectedBalance)}
                            </td>
                          )}
                          {canWrite && <td className="py-2 px-3" />}
                        </tr>
                      )}
                      {rowVisible && (<>
                      <tr
                        className={clsx(
                          'border-b border-slate-100',
                          t.projected ? 'bg-sky-50/40 hover:bg-sky-50' : 'hover:bg-slate-50',
                        )}
                      >
                        <td className={clsx('py-2 px-3 whitespace-nowrap', t.projected ? 'text-sky-800' : 'text-slate-700')}>
                          {formatDateBR(t.transactionDate)}
                        </td>
                        <td className="py-2 px-3 text-slate-700">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={clsx(t.projected && 'text-sky-900')}>{t.description}</span>
                            {/* Previsão e fato não podem ter a mesma cara: a linha
                                prevista diz o que é antes de dizer quanto vale. */}
                            {t.projected && (
                              <span className="inline-flex items-center gap-1 text-xs bg-sky-100 text-sky-700 border border-sky-200 rounded-full px-2 py-0.5 font-medium">
                                <CalendarClock size={11} /> Previsto
                              </span>
                            )}
                            {/* A previsão agrupa por dia e bandeira, que é como o
                                crédito chega. O detalhe abre igual ao depósito
                                realizado — mas sem ida ao servidor: as parcelas
                                já vieram no extrato. */}
                            {t.projected && (t.projectedItems?.length ?? 0) > 0 && (
                              <button
                                onClick={() => toggleProjection(projectionKey(t))}
                                className="inline-flex items-center gap-1 text-xs bg-sky-50 text-sky-700 border border-sky-200 rounded-full px-2 py-0.5 font-medium hover:bg-sky-100"
                                title="Ver as parcelas previstas para este dia"
                              >
                                {expandedProjections.has(projectionKey(t)) ? (
                                  <ChevronDown size={11} />
                                ) : (
                                  <ChevronRight size={11} />
                                )}
                                <Layers size={11} />
                                {t.projectedItems!.length} parcela{t.projectedItems!.length > 1 ? 's' : ''}
                              </button>
                            )}
                            {/* Previsto para uma data que já passou e ainda não
                                recebido: dinheiro que deveria ter entrado. É o
                                caso que a conciliação existe para achar. */}
                            {t.overdue && (
                              <span
                                className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5 font-medium"
                                title="Venceu e não foi conciliado. Não entra no saldo com previsão: na maioria das vezes o crédito já está na conta e falta apenas a baixa"
                              >
                                <AlertTriangle size={11} /> Não recebido
                              </span>
                            )}
                            {t.overdue && (
                              <button
                                type="button"
                                className="text-xs text-emerald-700 hover:underline"
                                onClick={() => {
                                  setGuideSection(STATEMENT_GUIDE_OVERDUE_ANCHOR);
                                  setView('guide');
                                }}
                              >
                                Como resolver
                              </button>
                            )}
                            {t.idCreditCardsInvoices != null && (
                              <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 font-medium">
                                <CreditCard size={11} /> Fatura Cartão
                              </span>
                            )}
                            {t.transactionGroup && (
                              <span className="inline-flex items-center text-xs bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">
                                Parcelado
                              </span>
                            )}
                            {/* Rateado: o valor é um só no extrato, mas foi dividido
                                entre categorias — abrir mostra as partes. */}
                            {t.allocated && (
                              <button
                                onClick={() => toggleAllocations(t.transactionId)}
                                className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-2 py-0.5 font-medium hover:bg-indigo-100"
                                title={allocationSummary(t.allocations ?? [])}
                              >
                                {expandedAllocations.has(t.transactionId) ? (
                                  <ChevronDown size={11} />
                                ) : (
                                  <ChevronRight size={11} />
                                )}
                                <Split size={11} /> Rateado · {t.allocations?.length ?? 0}
                              </button>
                            )}
                            {/* Recorrência: enquanto o lançamento for do gerador, o
                                processo das 05:00 atualiza valor e data pela definição.
                                Assim que alguém ajusta, ele trava — e o badge muda,
                                porque essa é justamente a dúvida de quem olha a tela
                                ("esse valor é a estimativa ou o real?"). */}
                            {t.recurringTransactionId != null && !t.recurringLocked && (
                              <span
                                className="inline-flex items-center text-xs bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 font-medium"
                                title="Gerado da recorrência — valor e data ainda são atualizados pela definição do lançamento recorrente"
                              >
                                Recorrente
                              </span>
                            )}
                            {t.recurringLocked && (
                              <span
                                className="inline-flex items-center text-xs bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 font-medium"
                                title="Ajustado manualmente — o processo diário não altera mais este lançamento"
                              >
                                Recorrente · ajustado
                              </span>
                            )}
                            {/* Provisão de fornecedor: gerada das parcelas do documento
                                de compra. É um lançamento normal (editável, entra no
                                saldo), mas a edição pode ser sobrescrita se as parcelas
                                do documento mudarem. */}
                            {t.purchaseOrderId != null && (
                              <RouterLink
                                to={`/purchase-orders/${t.purchaseOrderId}`}
                                className="inline-flex items-center text-xs bg-violet-100 text-violet-700 rounded-full px-2 py-0.5 font-medium hover:underline"
                                title={
                                  t.provisionGenerated
                                    ? 'Gerado das parcelas do Pedido de Compra — editar aqui pode ser sobrescrito se as parcelas mudarem'
                                    : 'Lançamento vinculado à mão ao pedido de compra'
                                }
                              >
                                {t.provisionGenerated ? 'Provisionada' : 'Vinculada'} · PC {t.purchaseOrderId}
                              </RouterLink>
                            )}
                            {t.materialReceiptId != null && (
                              <span
                                className="inline-flex items-center text-xs bg-violet-100 text-violet-700 rounded-full px-2 py-0.5 font-medium"
                                title="Gerado das parcelas do Recebimento de Materiais — editar aqui pode ser sobrescrito se as parcelas mudarem"
                              >
                                Provisionada · Receb. {t.materialReceiptId}
                              </span>
                            )}
                            {/* Fornecedor sem documento: a única pista de quem
                                recebeu o pagamento. Com PC/recebimento o badge
                                já diz. */}
                            {t.providerName && t.purchaseOrderId == null && t.materialReceiptId == null && (
                              <span className="block w-full text-xs text-slate-400">{t.providerName}</span>
                            )}
                            {/* Crédito de adquirente: dá para abrir e ver as vendas
                                que compõem o depósito. O botão só aparece onde há o
                                que abrir. */}
                            {t.cardSettlementId != null && (
                              <button
                                onClick={() => toggleSettlement(t.transactionId)}
                                className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5 font-medium hover:bg-emerald-100"
                                title="Ver as vendas que compõem este depósito"
                              >
                                {expandedSettlements.has(t.transactionId) ? (
                                  <ChevronDown size={11} />
                                ) : (
                                  <ChevronRight size={11} />
                                )}
                                <Layers size={11} /> Depósito
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-slate-500">
                          {t.categoryLabel ?? '—'}
                          {t.allocated && (t.allocations?.length ?? 0) > 1 && (
                            <span className="ml-1 text-xs text-indigo-600" title="Categoria principal; as demais partes estão no rateio">
                              +{(t.allocations?.length ?? 1) - 1}
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-center">
                          <span className={clsx('inline-flex text-xs rounded-full px-2 py-0.5 font-medium', activityTypeBadgeColor(t.activityType))}>
                            {activityTypeLabel(t.activityType)}
                          </span>
                        </td>
                        <td className={clsx('py-2 px-3 text-right font-medium whitespace-nowrap', t.amount < 0 ? 'text-red-600' : 'text-emerald-600')}>
                          {formatCurrencyBRL(t.amount)}
                        </td>
                        {/* O saldo realizado ignora a previsão: é ele que bate com
                            o extrato do banco. Na linha prevista ele não avança,
                            e mostrá-lo apagado deixa isso explícito. */}
                        <td className={clsx(
                          'py-2 px-3 text-right whitespace-nowrap',
                          t.projected ? 'text-slate-300' : t.runningBalance < 0 ? 'text-red-600' : 'text-emerald-600',
                        )}>
                          {formatCurrencyBRL(t.runningBalance)}
                        </td>
                        {/* Na linha vencida o saldo com previsão também não
                            avança — somá-la contaria duas vezes o crédito que
                            já entrou. Apagada, como na coluna do realizado. */}
                        {hasProjection && (
                          <td className={clsx(
                            'py-2 px-3 text-right whitespace-nowrap font-medium',
                            t.overdue
                              ? 'text-slate-300'
                              : (t.projectedBalance ?? t.runningBalance) < 0 ? 'text-red-600' : 'text-sky-700',
                          )}>
                            {formatCurrencyBRL(t.projectedBalance ?? t.runningBalance)}
                          </td>
                        )}
                        {canWrite && (
                          <td className="py-2 px-3">
                            {/* Previsão não se edita nem se exclui: ela não existe
                                como lançamento. Sai da tela sozinha quando o
                                dinheiro entra e a parcela é liquidada. */}
                            {t.projected ? (
                              <div className="text-right text-xs text-slate-400 pr-1" title="Previsão da agenda de recebíveis: some quando o crédito for conciliado">
                                —
                              </div>
                            ) : (
                            <div className="flex items-center justify-end gap-1">
                              <button
                                className="p-1.5 text-slate-400 hover:text-emerald-600 rounded"
                                title="Editar"
                                onClick={() => handleEditClick(t)}
                              >
                                <Edit2 size={15} />
                              </button>
                              <button
                                className="p-1.5 text-slate-400 hover:text-red-500 rounded"
                                title="Excluir"
                                onClick={() => handleDeleteClick(t)}
                                disabled={busy}
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                            )}
                          </td>
                        )}
                      </tr>
                      {t.cardSettlementId != null && expandedSettlements.has(t.transactionId) && (
                        <CardSettlementDetailRow
                          transactionId={t.transactionId}
                          colSpan={columnCount}
                        />
                      )}
                      {t.allocated && expandedAllocations.has(t.transactionId) && (
                        <tr className="bg-indigo-50/60 border-b border-indigo-100">
                          <td colSpan={columnCount} className="px-6 py-3">
                            <div className="text-xs font-medium text-indigo-800 mb-2">
                              Rateio de {formatCurrencyBRL(t.amount)} entre {t.allocations?.length ?? 0} categorias
                            </div>
                            <table className="w-full text-xs">
                              <tbody>
                                {(t.allocations ?? []).map((a) => (
                                  <tr key={a.allocationId} className="border-t border-indigo-100/70">
                                    <td className="py-1 pr-3 text-slate-700">{a.categoryLabel ?? `Categoria ${a.accountingPlanErpId}`}</td>
                                    <td className="py-1 pr-3 text-slate-500">{a.note ?? ''}</td>
                                    <td className={clsx('py-1 text-right font-medium whitespace-nowrap', a.amount < 0 ? 'text-red-600' : 'text-emerald-600')}>
                                      {formatCurrencyBRL(a.amount)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                      {t.projected && expandedProjections.has(projectionKey(t)) && (
                        <tr className="bg-sky-50/60 border-b border-sky-100">
                          <td colSpan={columnCount} className="px-6 py-3">
                            <p className="text-xs text-sky-800 mb-2">
                              Parcelas que a Cielo deve creditar neste dia. A previsão sai do extrato
                              quando o crédito for conciliado.
                            </p>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-sky-700/70 border-b border-sky-200">
                                  <th className="text-left font-medium py-1">Venda</th>
                                  <th className="text-left font-medium py-1">Parcela</th>
                                  <th className="text-right font-medium py-1">Líquido previsto</th>
                                </tr>
                              </thead>
                              <tbody>
                                {t.projectedItems!.map((item) => (
                                  <tr key={item.cardReceivableId} className="border-b border-sky-100 last:border-0">
                                    <td className="py-1.5">
                                      {item.orderId ? (
                                        <>
                                          <a
                                            href={`/sales-orders/${item.orderId}`}
                                            className="text-sky-700 hover:underline"
                                          >
                                            Pedido {item.orderId}
                                          </a>
                                          {/* Proposta resolvida para o pedido (OrderNumEcommerce
                                              = 'CRM-{id}'): o pedido é o link, a proposta é o
                                              contexto de onde a venda nasceu. */}
                                          {item.comercialProposalId && (
                                            <span className="text-slate-500"> (Proposta {item.comercialProposalId})</span>
                                          )}
                                        </>
                                      ) : item.comercialProposalId ? (
                                        <span className="text-slate-600">Proposta {item.comercialProposalId}</span>
                                      ) : (
                                        /* Captura sem pedido nem proposta: acontece
                                           nas vendas antigas do site, migradas sem
                                           vínculo único. Melhor dizer que não se
                                           sabe do que inventar uma origem. */
                                        <span className="text-slate-400">venda não identificada</span>
                                      )}
                                    </td>
                                    <td className="py-1.5 text-slate-600">{item.installmentLabel || '—'}</td>
                                    <td className="py-1.5 text-right font-medium text-sky-800">
                                      {formatCurrencyBRL(item.netAmount)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="border-t border-sky-200">
                                  <td colSpan={2} className="py-1.5 text-sky-700">Total do dia</td>
                                  <td className="py-1.5 text-right font-semibold text-sky-800">
                                    {formatCurrencyBRL(t.amount)}
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </td>
                        </tr>
                      )}
                      </>)}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default FinancialTransactionsView;
